import {useEffect} from 'react';

export type TEmbedName = 'auth' | 'build' | 'preview' | 'send' | 'sign' | 'view';

type TMessageDetail = Record<string, string | number | boolean | undefined>;

// Any page can frame an embed, so messages carry ids and event names only, never tokens or personal details.
export const postToHost = (embed: TEmbedName, event: string, detail: TMessageDetail = {}) => {
  if (window.parent !== window) {
    window.parent.postMessage({...detail, source: 'verdocs', version: 1, embed, event}, '*');
  }
};

export const postErrorToHost = (embed: TEmbedName, e: any, detail: TMessageDetail = {}) =>
  postToHost(embed, 'error', {...detail, message: e.detail?.message, status: e.detail?.statusCode});

// Hosts can't see how tall we are, so we tell them whenever that changes and they can size the frame to fit.
export const useHostResize = (embed: TEmbedName) => {
  useEffect(() => {
    if (window.parent === window) {
      return;
    }

    let lastHeight = 0;
    let frame = 0;
    const report = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Signing and viewing scroll inside themselves, and some components clip instead, so we add back what they hide
        let hidden = 0;
        document.querySelectorAll<HTMLElement>('#root *').forEach((el) => {
          const overflow = el.scrollHeight - el.clientHeight;
          if (overflow > hidden) {
            const overflowY = getComputedStyle(el).overflowY;
            // Only the embed's own component counts when clipping, since fields inside it clip their text on purpose
            let isTopComponent = overflowY === 'hidden' && el.tagName.startsWith('VERDOCS-');
            for (let parent = el.parentElement; isTopComponent && parent; parent = parent.parentElement) {
              isTopComponent = !parent.tagName.startsWith('VERDOCS-');
            }

            if (overflowY === 'auto' || overflowY === 'scroll' || isTopComponent) {
              hidden = overflow;
            }
          }
        });

        const height = Math.ceil(document.documentElement.scrollHeight + hidden);
        if (height !== lastHeight) {
          lastHeight = height;
          postToHost(embed, 'resize', {height});
        }
      });
    };

    // Pages arriving and images loading change our height without resizing the document itself
    const resizes = new ResizeObserver(report);
    const mutations = new MutationObserver(report);
    resizes.observe(document.documentElement);
    mutations.observe(document.body, {childList: true, subtree: true});
    document.addEventListener('load', report, true);
    report();

    return () => {
      cancelAnimationFrame(frame);
      resizes.disconnect();
      mutations.disconnect();
      document.removeEventListener('load', report, true);
    };
  }, [embed]);
};
